import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { gmailApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';

const GmailCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [error, setError] = React.useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent duplicate processing (React StrictMode causes double-mount in dev)
      if (hasProcessed.current) {
        console.log('Gmail callback already processed, skipping duplicate call');
        return;
      }

      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        hasProcessed.current = true;
        setError('Gmail connection was cancelled or failed');
        showError('Gmail connection cancelled');
        setTimeout(() => navigate('/settings?tab=email&gmail=1'), 3000);
        return;
      }

      if (code) {
        hasProcessed.current = true;
        try {
          console.log('Received OAuth code, connecting to Gmail...');
          await gmailApi.connect(code);
          showSuccess('Gmail connected successfully! You can now fetch transaction emails.');
          setTimeout(() => navigate('/settings?tab=email&gmail=1'), 2000);
        } catch (error) {
          console.error('Gmail connection error:', error);
          setError(error.response?.data?.message || 'Failed to complete Gmail connection');
          showError('Failed to complete Gmail connection');
          setTimeout(() => navigate('/settings?tab=email&gmail=1'), 3000);
        }
      } else {
        hasProcessed.current = true;
        setError('No authorization code received');
        setTimeout(() => navigate('/settings?tab=email&gmail=1'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, showSuccess, showError]);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh',
        gap: 3
      }}
    >
      {!error ? (
        <>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Connecting Gmail...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we complete the connection
          </Typography>
        </>
      ) : (
        <>
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Redirecting to settings...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default GmailCallback;

